import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Plus, X, GripVertical } from "lucide-react";
import { ImageUpload } from "./ImageUpload";
import { useDraggableHotspots } from "./useDraggableHotspots";

interface BlockEditFormProps {
  blockType: string;
  content: Record<string, any>;
  onChange: (content: Record<string, any>) => void;
}

export function BlockEditForm({ blockType, content, onChange }: BlockEditFormProps) {
  const update = (key: string, value: any) => onChange({ ...content, [key]: value });

  switch (blockType) {
    case "text_section":
      return (
        <div className="space-y-4">
          <div>
            <Label className="font-sans text-xs">Title</Label>
            <Input value={content.title || ""} onChange={(e) => update("title", e.target.value)} />
          </div>
          <div>
            <Label className="font-sans text-xs">Body</Label>
            <Textarea rows={6} value={content.body || ""} onChange={(e) => update("body", e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={!!content.showDivider} onCheckedChange={(v) => update("showDivider", v)} />
            <Label className="font-sans text-xs">Show divider</Label>
          </div>
        </div>
      );

    case "image":
      return (
        <div className="space-y-4">
          <ImageUpload label="Image" value={content.url || ""} onChange={(v) => update("url", v)} />
          <div>
            <Label className="font-sans text-xs">Caption</Label>
            <Input value={content.caption || ""} onChange={(e) => update("caption", e.target.value)} />
          </div>
          <div>
            <Label className="font-sans text-xs">Alignment</Label>
            <Select value={content.alignment || "center"} onValueChange={(v) => update("alignment", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="left">Left</SelectItem>
                <SelectItem value="center">Center</SelectItem>
                <SelectItem value="right">Right</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );

    case "two_column":
      return (
        <div className="space-y-4">
          <ImageUpload label="Image" value={content.imageUrl || ""} onChange={(v) => update("imageUrl", v)} />
          <div>
            <Label className="font-sans text-xs">Title</Label>
            <Input value={content.title || ""} onChange={(e) => update("title", e.target.value)} />
          </div>
          <div>
            <Label className="font-sans text-xs">Text</Label>
            <Textarea rows={4} value={content.text || ""} onChange={(e) => update("text", e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={!!content.reversed} onCheckedChange={(v) => update("reversed", v)} />
            <Label className="font-sans text-xs">Reverse layout</Label>
          </div>
        </div>
      );

    case "material_reference":
      return <MaterialReferenceForm content={content} onChange={onChange} />;

    case "gallery": {
      const images: any[] = content.images || [];
      const updateImage = (idx: number, key: string, val: any) => {
        const updated = [...images];
        updated[idx] = { ...updated[idx], [key]: val };
        update("images", updated);
      };
      const addImage = () => update("images", [...images, { url: "", caption: "", hotspots: [] }]);
      const removeImage = (idx: number) => update("images", images.filter((_, i) => i !== idx));

      return (
        <div className="space-y-4">
          {images.map((img, i) => (
            <GalleryImageEditor
              key={i}
              index={i}
              img={img}
              onUpdate={(key, val) => updateImage(i, key, val)}
              onRemove={() => removeImage(i)}
            />
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addImage} className="gap-1 text-xs">
            <Plus size={12} /> Add Image
          </Button>
        </div>
      );
    }

    case "cta":
      return (
        <div className="space-y-4">
          <div>
            <Label className="font-sans text-xs">Headline</Label>
            <Input value={content.headline || ""} onChange={(e) => update("headline", e.target.value)} />
          </div>
          <div>
            <Label className="font-sans text-xs">Subtext</Label>
            <Input value={content.subtext || ""} onChange={(e) => update("subtext", e.target.value)} />
          </div>
          <div>
            <Label className="font-sans text-xs">Button Text</Label>
            <Input value={content.buttonText || ""} onChange={(e) => update("buttonText", e.target.value)} />
          </div>
          <div>
            <Label className="font-sans text-xs">Button Link</Label>
            <Input value={content.buttonLink || ""} onChange={(e) => update("buttonLink", e.target.value)} />
          </div>
        </div>
      );

    case "image_hotspot":
      return <ImageHotspotForm content={content} onChange={onChange} />;

    case "bullet_list": {
      const items: string[] = content.items || [];
      return (
        <div className="space-y-4">
          <div>
            <Label className="font-sans text-xs">Title</Label>
            <Input value={content.title || ""} onChange={(e) => update("title", e.target.value)} />
          </div>
          <div>
            <Label className="font-sans text-xs">Subtitle</Label>
            <Input value={content.subtitle || ""} onChange={(e) => update("subtitle", e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={!!content.showDivider} onCheckedChange={(v) => update("showDivider", v)} />
            <Label className="font-sans text-xs">Show divider</Label>
          </div>
          <div>
            <Label className="font-sans text-xs mb-2 block">Items</Label>
            {items.map((item, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <Input value={item} onChange={(e) => { const u = [...items]; u[i] = e.target.value; update("items", u); }} className="flex-1" />
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9" onClick={() => update("items", items.filter((_, j) => j !== i))}><X size={14} /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => update("items", [...items, ""])} className="gap-1 text-xs"><Plus size={12} /> Add item</Button>
          </div>
        </div>
      );
    }

    case "quote":
      return (
        <div className="space-y-4">
          <div>
            <Label className="font-sans text-xs">Quote</Label>
            <Textarea rows={4} value={content.quote || ""} onChange={(e) => update("quote", e.target.value)} />
          </div>
          <div>
            <Label className="font-sans text-xs">Attribution</Label>
            <Input value={content.attribution || ""} onChange={(e) => update("attribution", e.target.value)} />
          </div>
        </div>
      );

    case "specs": {
      const specs: any[] = content.specs || [];
      const updateSpec = (idx: number, key: string, val: string) => { const u = [...specs]; u[idx] = { ...u[idx], [key]: val }; update("specs", u); };
      return (
        <div className="space-y-4">
          <div>
            <Label className="font-sans text-xs">Title</Label>
            <Input value={content.title || ""} onChange={(e) => update("title", e.target.value)} />
          </div>
          <div>
            <Label className="font-sans text-xs">Subtitle</Label>
            <Input value={content.subtitle || ""} onChange={(e) => update("subtitle", e.target.value)} />
          </div>
          <div>
            <Label className="font-sans text-xs mb-2 block">Specifications</Label>
            {specs.map((spec, i) => (
              <div key={i} className="border border-border p-3 mb-2 space-y-2 relative">
                <button onClick={() => update("specs", specs.filter((_, j) => j !== i))} className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"><X size={14} /></button>
                <Select value={spec.icon || ""} onValueChange={(v) => updateSpec(i, "icon", v)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select icon" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="area">Area (Scaling)</SelectItem>
                    <SelectItem value="context">Context (Landmark)</SelectItem>
                    <SelectItem value="layout">Layout (Panel)</SelectItem>
                    <SelectItem value="typology">Typology (Building)</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Label" value={spec.label || ""} onChange={(e) => updateSpec(i, "label", e.target.value)} className="h-8 text-xs" />
                <Input placeholder="Value" value={spec.value || ""} onChange={(e) => updateSpec(i, "value", e.target.value)} className="h-8 text-xs" />
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => update("specs", [...specs, { icon: "", label: "", value: "" }])} className="gap-1 text-xs"><Plus size={12} /> Add spec</Button>
          </div>
        </div>
      );
    }

    case "challenges": {
      const challenges: any[] = content.challenges || [];
      const updateChallenge = (idx: number, key: string, val: string) => { const u = [...challenges]; u[idx] = { ...u[idx], [key]: val }; update("challenges", u); };
      return (
        <div className="space-y-4">
          <div>
            <Label className="font-sans text-xs">Title</Label>
            <Input value={content.title || ""} onChange={(e) => update("title", e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={!!content.showDivider} onCheckedChange={(v) => update("showDivider", v)} />
            <Label className="font-sans text-xs">Show divider</Label>
          </div>
          <div>
            <Label className="font-sans text-xs mb-2 block">Challenges</Label>
            {challenges.map((c, i) => (
              <div key={i} className="border border-border p-3 mb-2 space-y-2 relative">
                <button onClick={() => update("challenges", challenges.filter((_, j) => j !== i))} className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"><X size={14} /></button>
                <Input placeholder="Title" value={c.title || ""} onChange={(e) => updateChallenge(i, "title", e.target.value)} className="h-8 text-xs" />
                <Textarea placeholder="Description" rows={2} value={c.description || ""} onChange={(e) => updateChallenge(i, "description", e.target.value)} className="text-xs" />
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => update("challenges", [...challenges, { title: "", description: "" }])} className="gap-1 text-xs"><Plus size={12} /> Add challenge</Button>
          </div>
        </div>
      );
    }

    case "outcomes": {
      const outcomes: any[] = content.outcomes || [];
      const updateOutcome = (idx: number, key: string, val: string) => { const u = [...outcomes]; u[idx] = { ...u[idx], [key]: val }; update("outcomes", u); };
      return (
        <div className="space-y-4">
          <div>
            <Label className="font-sans text-xs">Title</Label>
            <Input value={content.title || ""} onChange={(e) => update("title", e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={!!content.showDivider} onCheckedChange={(v) => update("showDivider", v)} />
            <Label className="font-sans text-xs">Show divider</Label>
          </div>
          <div>
            <Label className="font-sans text-xs mb-2 block">Outcomes</Label>
            {outcomes.map((o, i) => (
              <div key={i} className="border border-border p-3 mb-2 space-y-2 relative">
                <button onClick={() => update("outcomes", outcomes.filter((_, j) => j !== i))} className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"><X size={14} /></button>
                <ImageUpload label="Image" value={o.imageUrl || ""} onChange={(v) => updateOutcome(i, "imageUrl", v)} />
                <Input placeholder="Label" value={o.label || ""} onChange={(e) => updateOutcome(i, "label", e.target.value)} className="h-8 text-xs" />
                <Textarea placeholder="Description" rows={2} value={o.description || ""} onChange={(e) => updateOutcome(i, "description", e.target.value)} className="text-xs" />
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => update("outcomes", [...outcomes, { imageUrl: "", label: "", description: "" }])} className="gap-1 text-xs"><Plus size={12} /> Add outcome</Button>
          </div>
        </div>
      );
    }

    case "result": {
      const statements: string[] = content.statements || [];
      return (
        <div className="space-y-4">
          <div>
            <Label className="font-sans text-xs">Title</Label>
            <Input value={content.title || ""} onChange={(e) => update("title", e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={!!content.showDivider} onCheckedChange={(v) => update("showDivider", v)} />
            <Label className="font-sans text-xs">Show divider</Label>
          </div>
          <div>
            <Label className="font-sans text-xs mb-2 block">Statements</Label>
            {statements.map((s, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <Input value={s} onChange={(e) => { const u = [...statements]; u[i] = e.target.value; update("statements", u); }} className="flex-1" />
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9" onClick={() => update("statements", statements.filter((_, j) => j !== i))}><X size={14} /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => update("statements", [...statements, ""])} className="gap-1 text-xs"><Plus size={12} /> Add statement</Button>
          </div>
        </div>
      );
    }

    case "scenarios": {
      const tags: string[] = content.tags || [];
      return (
        <div className="space-y-4">
          <div>
            <Label className="font-sans text-xs">Title</Label>
            <Input value={content.title || ""} onChange={(e) => update("title", e.target.value)} />
          </div>
          <div>
            <Label className="font-sans text-xs">Subtitle</Label>
            <Input value={content.subtitle || ""} onChange={(e) => update("subtitle", e.target.value)} />
          </div>
          <div>
            <Label className="font-sans text-xs mb-2 block">Tags</Label>
            {tags.map((tag, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <Input value={tag} onChange={(e) => { const u = [...tags]; u[i] = e.target.value; update("tags", u); }} className="flex-1" />
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9" onClick={() => update("tags", tags.filter((_, j) => j !== i))}><X size={14} /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => update("tags", [...tags, ""])} className="gap-1 text-xs"><Plus size={12} /> Add tag</Button>
          </div>
        </div>
      );
    }

    case "material_strategy": {
      const sections: any[] = content.sections || [];
      const updateSection = (idx: number, key: string, val: any) => { const u = [...sections]; u[idx] = { ...u[idx], [key]: val }; update("sections", u); };
      return (
        <div className="space-y-4">
          <div>
            <Label className="font-sans text-xs">Title</Label>
            <Input value={content.title || ""} onChange={(e) => update("title", e.target.value)} />
          </div>
          <div>
            <Label className="font-sans text-xs">Subtitle</Label>
            <Input value={content.subtitle || ""} onChange={(e) => update("subtitle", e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={!!content.showDivider} onCheckedChange={(v) => update("showDivider", v)} />
            <Label className="font-sans text-xs">Show divider</Label>
          </div>
          <div>
            <Label className="font-sans text-xs mb-2 block">Sections</Label>
            {sections.map((sec, i) => (
              <div key={i} className="border border-border p-3 mb-3 space-y-2 relative">
                <button onClick={() => update("sections", sections.filter((_, j) => j !== i))} className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"><X size={14} /></button>
                <p className="font-sans text-xs font-medium text-foreground">Section {i + 1}</p>
                <Input placeholder="Layer label" value={sec.label || ""} onChange={(e) => updateSection(i, "label", e.target.value)} className="h-8 text-xs" />
                <Input placeholder="Title" value={sec.title || ""} onChange={(e) => updateSection(i, "title", e.target.value)} className="h-8 text-xs" />
                <Textarea placeholder="Description" rows={2} value={sec.description || ""} onChange={(e) => updateSection(i, "description", e.target.value)} className="text-xs" />
                <Input placeholder="Strategic value" value={sec.strategicValue || ""} onChange={(e) => updateSection(i, "strategicValue", e.target.value)} className="h-8 text-xs" />
                <ImageUpload label="Image" value={sec.imageUrl || ""} onChange={(v) => updateSection(i, "imageUrl", v)} />
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => update("sections", [...sections, { label: "", title: "", description: "", imageUrl: "" }])} className="gap-1 text-xs"><Plus size={12} /> Add section</Button>
          </div>
        </div>
      );
    }

    default:
      return <p className="text-sm text-muted-foreground">Unknown block type: {blockType}</p>;
  }
}

function MaterialReferenceForm({ content, onChange }: { content: Record<string, any>; onChange: (c: Record<string, any>) => void }) {
  const [search, setSearch] = useState("");
  const selectedIds: string[] = content.materialIds || [];
  const appZones: Record<string, string> = content.applicationZones || {};

  const { data: allMaterials } = useQuery({
    queryKey: ["all-materials-for-editor"],
    queryFn: async () => {
      const { data, error } = await supabase.from("materials").select("id, name, category, texture, image_url").order("name");
      if (error) throw error;
      return data;
    },
  });

  const toggle = (id: string) => {
    const next = selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id];
    onChange({ ...content, materialIds: next });
  };

  const updateZone = (id: string, val: string) => {
    onChange({ ...content, applicationZones: { ...appZones, [id]: val } });
  };

  const filtered = (allMaterials || []).filter((m) =>
    !search || m.name.toLowerCase().includes(search.toLowerCase()) || m.category.toLowerCase().includes(search.toLowerCase())
  );

  const selected = filtered.filter((m) => selectedIds.includes(m.id));
  const unselected = filtered.filter((m) => !selectedIds.includes(m.id));

  return (
    <div className="space-y-4">
      <Input placeholder="Search materials…" value={search} onChange={(e) => setSearch(e.target.value)} />

      <div className="max-h-[400px] overflow-y-auto space-y-1">
        {selected.map((mat) => (
          <div key={mat.id} className="flex items-start gap-3 border border-primary/30 bg-primary/5 p-3">
            <Checkbox checked onCheckedChange={() => toggle(mat.id)} className="mt-0.5" />
            {mat.image_url && <img src={mat.image_url} alt="" className="h-10 w-10 object-cover flex-shrink-0" />}
            <div className="flex-1 min-w-0 space-y-1">
              <p className="font-sans text-xs font-medium text-foreground truncate">{mat.name}</p>
              <p className="font-sans text-[11px] text-muted-foreground">{mat.category}{mat.texture ? ` — ${mat.texture}` : ""}</p>
              <Input
                placeholder="Application zone (optional)"
                value={appZones[mat.id] || ""}
                onChange={(e) => updateZone(mat.id, e.target.value)}
                className="h-7 text-xs"
              />
            </div>
          </div>
        ))}
        {unselected.map((mat) => (
          <div key={mat.id} className="flex items-center gap-3 border border-border p-3 hover:bg-muted/50 cursor-pointer" onClick={() => toggle(mat.id)}>
            <Checkbox checked={false} onCheckedChange={() => toggle(mat.id)} />
            {mat.image_url && <img src={mat.image_url} alt="" className="h-10 w-10 object-cover flex-shrink-0" />}
            <div className="min-w-0">
              <p className="font-sans text-xs font-medium text-foreground truncate">{mat.name}</p>
              <p className="font-sans text-[11px] text-muted-foreground">{mat.category}{mat.texture ? ` — ${mat.texture}` : ""}</p>
            </div>
          </div>
        ))}
        {!filtered.length && <p className="text-xs text-muted-foreground py-4 text-center">No materials found</p>}
      </div>
    </div>
  );
}

function ImageHotspotForm({ content, onChange }: { content: Record<string, any>; onChange: (c: Record<string, any>) => void }) {
  const [search, setSearch] = useState("");
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const hotspots: { x: number; y: number; materialId: string }[] = content.hotspots || [];

  const { data: allMaterials } = useQuery({
    queryKey: ["all-materials-for-editor"],
    queryFn: async () => {
      const { data, error } = await supabase.from("materials").select("id, name, category, texture, image_url").order("name");
      if (error) throw error;
      return data;
    },
  });

  const update = (key: string, value: any) => onChange({ ...content, [key]: value });

  const updateHotspot = (idx: number, key: string, val: any) => {
    const updated = [...hotspots];
    updated[idx] = { ...updated[idx], [key]: val };
    update("hotspots", updated);
  };

  const removeHotspot = (idx: number) => {
    update("hotspots", hotspots.filter((_, i) => i !== idx));
    if (selectedIdx === idx) setSelectedIdx(null);
    else if (selectedIdx !== null && selectedIdx > idx) setSelectedIdx(selectedIdx - 1);
  };

  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (drag.draggingIdx !== null) return;
    // If clicking on a dot, don't add
    if ((e.target as HTMLElement).closest("[data-hotspot-dot]")) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
    const newIdx = hotspots.length;
    update("hotspots", [...hotspots, { x, y, materialId: "" }]);
    setSelectedIdx(newIdx);
  };

  const drag = useDraggableHotspots(hotspots, (updated) => update("hotspots", updated));

  const filtered = (allMaterials || []).filter(
    (m) => !search || m.name.toLowerCase().includes(search.toLowerCase()) || m.category.toLowerCase().includes(search.toLowerCase())
  );

  const getMaterialName = (id: string) => {
    const mat = (allMaterials || []).find((m) => m.id === id);
    return mat ? mat.name : "";
  };

  return (
    <div className="space-y-4">
      <ImageUpload label="Image" value={content.imageUrl || ""} onChange={(v) => update("imageUrl", v)} />

      {content.imageUrl && (
        <>
          <p className="text-xs text-muted-foreground">Click on image to add a hotspot. Drag dots to reposition. Click a dot to select & edit.</p>
          <div
            ref={drag.containerRef}
            className="relative border-2 border-border cursor-crosshair select-none"
            onClick={handleImageClick}
            onMouseMove={drag.handleMouseMove}
            onMouseUp={drag.handleMouseUp}
            onMouseLeave={drag.handleMouseUp}
          >
            <img src={content.imageUrl} alt="" className="w-full h-auto block pointer-events-none" />
            {hotspots.map((spot, i) => (
              <div
                key={i}
                data-hotspot-dot
                className={`absolute group flex items-center justify-center transition-all ${
                  drag.draggingIdx === i
                    ? "z-20 scale-125"
                    : selectedIdx === i
                    ? "z-10 scale-110"
                    : "cursor-grab hover:scale-110"
                }`}
                style={{ left: `${spot.x}%`, top: `${spot.y}%`, transform: "translate(-50%, -50%)" }}
                onMouseDown={(e) => {
                  drag.handleDotMouseDown(e, i);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedIdx(selectedIdx === i ? null : i);
                }}
              >
                {/* Dot */}
                <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center shadow-sm transition-colors ${
                  selectedIdx === i
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-primary bg-white/90 text-primary"
                }`}>
                  <span className="text-[9px] font-bold">{i + 1}</span>
                </div>

                {/* Material label */}
                {spot.materialId && (
                  <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 whitespace-nowrap bg-background/95 border border-border rounded px-1.5 py-0.5 text-[9px] font-medium text-foreground shadow-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    {getMaterialName(spot.materialId)}
                  </div>
                )}

                {/* Delete button on hover */}
                <button
                  className="absolute -top-2 -right-2 h-4 w-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:scale-110"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeHotspot(i);
                  }}
                >
                  <X size={8} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Selected hotspot editor */}
      {selectedIdx !== null && selectedIdx < hotspots.length && (
        <div className="border border-primary/30 bg-primary/5 p-4 space-y-3 relative rounded">
          <div className="flex items-center justify-between">
            <p className="font-sans text-xs font-medium text-foreground">
              Hotspot {selectedIdx + 1} <span className="text-muted-foreground">({hotspots[selectedIdx].x}%, {hotspots[selectedIdx].y}%)</span>
            </p>
            <button onClick={() => removeHotspot(selectedIdx)} className="text-destructive hover:text-destructive/80 text-xs flex items-center gap-1">
              <X size={12} /> Remove
            </button>
          </div>
          <div>
            <Label className="font-sans text-xs">Material</Label>
            <Select value={hotspots[selectedIdx].materialId || ""} onValueChange={(v) => updateHotspot(selectedIdx, "materialId", v)}>
              <SelectTrigger><SelectValue placeholder="Select material…" /></SelectTrigger>
              <SelectContent>
                <div className="p-2">
                  <Input placeholder="Search…" value={search} onChange={(e) => { e.stopPropagation(); setSearch(e.target.value); }} className="h-8 text-xs" />
                </div>
                {filtered.map((mat) => (
                  <SelectItem key={mat.id} value={mat.id}>
                    {mat.name} — {mat.category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Hotspot list summary */}
      {hotspots.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">{hotspots.length} hotspot{hotspots.length !== 1 ? "s" : ""}</p>
          <div className="flex flex-wrap gap-1">
            {hotspots.map((spot, i) => (
              <button
                key={i}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                  selectedIdx === i
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
                onClick={() => setSelectedIdx(selectedIdx === i ? null : i)}
              >
                {i + 1}. {spot.materialId ? getMaterialName(spot.materialId) : "Unassigned"}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function GalleryImageEditor({
  index,
  img,
  onUpdate,
  onRemove,
}: {
  index: number;
  img: any;
  onUpdate: (key: string, val: any) => void;
  onRemove: () => void;
}) {
  const [search, setSearch] = useState("");
  const [placingMode, setPlacingMode] = useState(false);
  const hotspots: { x: number; y: number; materialId: string }[] = img.hotspots || [];

  const { data: allMaterials } = useQuery({
    queryKey: ["all-materials-for-editor"],
    queryFn: async () => {
      const { data, error } = await supabase.from("materials").select("id, name, category, texture, image_url").order("name");
      if (error) throw error;
      return data;
    },
  });

  const updateHotspot = (idx: number, key: string, val: any) => {
    const updated = [...hotspots];
    updated[idx] = { ...updated[idx], [key]: val };
    onUpdate("hotspots", updated);
  };

  const removeHotspot = (idx: number) => {
    onUpdate("hotspots", hotspots.filter((_, i) => i !== idx));
  };

  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!placingMode || hotspots.length >= 5) return;
    if (drag.draggingIdx !== null) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
    onUpdate("hotspots", [...hotspots, { x, y, materialId: "" }]);
    setPlacingMode(false);
  };

  const drag = useDraggableHotspots(hotspots, (updated) => onUpdate("hotspots", updated));

  const filtered = (allMaterials || []).filter(
    (m) => !search || m.name.toLowerCase().includes(search.toLowerCase()) || m.category.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="border border-border p-4 space-y-3 relative">
      <button onClick={onRemove} className="absolute top-2 right-2 text-muted-foreground hover:text-foreground">
        <X size={14} />
      </button>
      <p className="font-sans text-xs font-medium text-foreground">Image {index + 1}</p>
      <ImageUpload label="Image" value={img.url || ""} onChange={(v) => onUpdate("url", v)} />
      <div>
        <Label className="font-sans text-xs">Caption</Label>
        <Input value={img.caption || ""} onChange={(e) => onUpdate("caption", e.target.value)} />
      </div>

      {/* Hotspot editor per gallery image */}
      {img.url && (
        <>
          <div className="border-t border-border pt-3 mt-3">
            <p className="font-sans text-xs font-medium text-muted-foreground mb-2">Hotspots (drag to reposition)</p>
          </div>
          <div
            ref={drag.containerRef}
            className={`relative border-2 transition-colors select-none ${placingMode ? "border-primary cursor-crosshair" : "border-border"}`}
            onClick={handleImageClick}
            onMouseMove={drag.handleMouseMove}
            onMouseUp={drag.handleMouseUp}
            onMouseLeave={drag.handleMouseUp}
          >
            <img src={img.url} alt="" className="w-full h-auto block pointer-events-none" />
            {hotspots.map((spot, i) => (
              <div
                key={i}
                className={`absolute h-5 w-5 rounded-full border-2 border-primary bg-white/90 shadow-sm flex items-center justify-center transition-shadow ${drag.draggingIdx === i ? "shadow-lg ring-2 ring-primary/40 scale-125 z-20" : "cursor-grab hover:shadow-md hover:scale-110"}`}
                style={{ left: `${spot.x}%`, top: `${spot.y}%`, transform: "translate(-50%, -50%)" }}
                onMouseDown={(e) => drag.handleDotMouseDown(e, i)}
              >
                <span className="text-[9px] font-bold text-primary">{i + 1}</span>
              </div>
            ))}
            {placingMode && (
              <div className="absolute inset-0 bg-primary/5 flex items-center justify-center">
                <span className="bg-background/90 px-3 py-1.5 rounded text-xs font-medium text-foreground shadow">
                  Click to place hotspot
                </span>
              </div>
            )}
          </div>
          <Button
            type="button"
            variant={placingMode ? "default" : "outline"}
            size="sm"
            onClick={() => setPlacingMode(!placingMode)}
            disabled={hotspots.length >= 5}
            className="gap-1 text-xs"
          >
            <Plus size={12} /> {placingMode ? "Cancel placing" : `Place hotspot${hotspots.length >= 5 ? " (max 5)" : ""}`}
          </Button>

          {hotspots.map((spot, i) => (
            <div key={i} className="border border-border p-3 space-y-2 relative">
              <button onClick={() => removeHotspot(i)} className="absolute top-2 right-2 text-muted-foreground hover:text-foreground">
                <X size={14} />
              </button>
              <p className="font-sans text-xs font-medium text-foreground">
                Hotspot {i + 1} <span className="text-muted-foreground">({spot.x}%, {spot.y}%)</span>
              </p>
              <div>
                <Label className="font-sans text-xs">Material</Label>
                <Select value={spot.materialId || ""} onValueChange={(v) => updateHotspot(i, "materialId", v)}>
                  <SelectTrigger><SelectValue placeholder="Select material…" /></SelectTrigger>
                  <SelectContent>
                    <div className="p-2">
                      <Input placeholder="Search…" value={search} onChange={(e) => { e.stopPropagation(); setSearch(e.target.value); }} className="h-8 text-xs" />
                    </div>
                    {filtered.map((mat) => (
                      <SelectItem key={mat.id} value={mat.id}>
                        {mat.name} — {mat.category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
